import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Grid from '@mui/material/Grid';
import Drawer from '@mui/material/Drawer';
import Container from '@mui/material/Container';
import Card from 'components/Card';
import Select from 'components/Select';
import Button from 'components/Button';
import Fab from 'components/Fab';
import Snackbar from 'components/Snackbar';
import Label from 'components/Label';
import { Text } from 'v3/components';
import Tooltip from 'v3/components/Tooltip';
import axios from 'axios';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import Checkbox from '../../../components/CheckBox';
import FormCadastroPerfil from './FormCadastroPerfil';
import { usePermission } from 'hooks';
import { FaExclamationCircle, FaRegLightbulb } from 'react-icons/fa';
import { cooperplaceApi } from 'services/api';
import ApplicationLayout from 'v3/layouts/Application';
import ProfilesAuditModal from './ProfilesAuditModal';

const drawerWidth = 330;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    zIndex: 2,
    paddingTop: '80px',
    overflow: 'initial',
  },
  drawerPaper: {
    width: drawerWidth,
    paddingTop: '80px',
    zIndex: 2,
    overflow: 'initial',
  },  
  content: {
    flexGrow: 1,
    padding: typeof theme.spacing === 'function' ? theme.spacing(1) : '8px',
    width: `calc(100% - ${0}px)`,
    transition: theme.transitions?.create?.('margin', {
      easing: theme.transitions?.easing?.sharp ?? 'cubic-bezier(0.4, 0, 0.6, 1)',
      duration: theme.transitions?.duration?.leavingScreen ?? 225,
    }),
    marginRight: -drawerWidth,
  },
  contentShift: {
    width: `calc(100% - ${drawerWidth}px)`,
  transition: theme.transitions?.create?.('margin', {
      easing: theme.transitions?.easing?.easeOut ?? 'cubic-bezier(0.4, 0, 0.6, 1)',
      duration: theme.transitions?.duration?.enteringScreen ?? 225,
    }) ?? 'margin 225ms cubic-bezier(0.4, 0, 0.6, 1)',
  marginRight: 0,
  },
  permissionsColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
    paddingTop: 30,
    [theme.breakpoints?.down?.('md') || '@media (max-width: 900px)']: {
      gridTemplateColumns: '1fr',
    },
  },
  permissionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  permissionsSectionHeader: {
    marginTop: 24,
    marginBottom: 10,
  },
  permissionsSectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8,
  },
  permissionsSectionTitle: {
    color: '#2F3B52',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  permissionsSectionIcon: {
    color: '#5B6B7F',
    fontSize: 16,
    cursor: 'pointer',
  },
  permissionsSectionDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#D8DDE0',
  },
  permissionCard: {
    border: '1.5px solid #C3CCD6',
    borderRadius: 8,
    boxShadow: 'none',
    backgroundColor: '#FFFFFF',
  },
  permissionCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8,
  },
  permissionCardDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#C9D2DC',
    marginBottom: 10,
  },
  permissionCardInfoIcon: {
    color: '#7A8994',
    fontSize: 14,
    cursor: 'pointer',
    flexShrink: 0,
  },
  permissionItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  permissionItemInfo: {
    color: '#68788C',
    fontSize: 14,
    cursor: 'pointer',
    marginLeft: 8,
    flexShrink: 0,
  },
  auditButton: {
    minWidth: 126,
    height: 40,
    borderRadius: 6,
    backgroundColor: '#E5EAEE',
    border: '1px solid #D8DDE0',
    boxShadow: 'none',
    color: '#2F3B52',
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 18px',
  },
}));

export default function PermissoesUsuarios() {
  const classes = useStyles();
  const navigate = useNavigate();
  usePermission('VISUALIZAR_PERMISSOES_USUARIOS', { redirect: true });
  const [state, setState] = useState({
    permissionsList: [],
    checkedPermissions: [],
  });
  const [profileList, setProfileList] = useState([]);

  const [values, setValues] = React.useState({
    selectedProfile: '',
  });
  const [snackbar, setSnackbar] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [formOpened, setFormOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    getProfileList();
  }, []);

  function normalizeGroupTitle(title) {
    return (title || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  function hasAccent(title) {
    return /[^\u0000-\u007f]/.test(title || '');
  }

  async function getProfileList() {
    const response = await axios.get(`${config.server}users-profiles`);
    if (response.status === 200) {
      const arrData = response.data;
      const arrFinal = [];
      for (let x = 0; x < arrData.length; x++) {
        arrFinal.push(arrData[x]);
      }

      setProfileList(arrFinal);
    }

    getPermissionsList();
  }

  async function getPermissionsList() {
    const response = await axios.get(`${config.server}permissions`);
    if (response.status === 200) {
      const arrData = [];
      let group = '';

      let itemAtual = { title: '', itens: [] };
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].group !== group) {
          if (i > 0) {
            arrData.push(itemAtual);
          }
          group = response.data[i].group;

          itemAtual = { title: group, itens: [] };
        }
        itemAtual.itens.push(response.data[i]);
      }
      arrData.push(itemAtual);

      const groupedByTitle = new Map();

      arrData.forEach((groupItem, index) => {
        const normalizedTitle = normalizeGroupTitle(groupItem.title);
        const currentItems = Array.isArray(groupItem.itens) ? groupItem.itens : [];
        const incomingHasAccent = hasAccent(groupItem.title);

        if (!groupedByTitle.has(normalizedTitle)) {
          groupedByTitle.set(normalizedTitle, {
            title: groupItem.title,
            itens: [...currentItems],
            originalIndex: index,
            hasAccent: incomingHasAccent,
          });
          return;
        }

        const existingGroup = groupedByTitle.get(normalizedTitle);
        const mergedBySlug = new Map();

        existingGroup.itens.forEach((item) => {
          mergedBySlug.set(item.slug || item.id, item);
        });
        currentItems.forEach((item) => {
          const key = item.slug || item.id;
          if (!mergedBySlug.has(key)) {
            mergedBySlug.set(key, item);
          }
        });

        if (!existingGroup.hasAccent && incomingHasAccent) {
          existingGroup.title = groupItem.title;
          existingGroup.originalIndex = index;
          existingGroup.hasAccent = true;
        }

        existingGroup.itens = Array.from(mergedBySlug.values());
      });

      const mergedGroups = Array.from(groupedByTitle.values())
        .sort((a, b) => a.originalIndex - b.originalIndex)
        .map(({ title, itens, originalIndex }) => ({ title, itens, originalIndex }));

      setState({ ...state, permissionsList: mergedGroups });
    }
  }

  async function changeProfile(event) {
    const profile_id = event;
    setProfileLoading(true);

    try {
      const response = await axios.get(`${config.server}users-profiles/${profile_id}`);

      const profile = response.data;

      const newState = { ...state, profileSelected: profile };

      const allChecks = document.getElementsByClassName('check-permission');

      for (let x = 0; x < allChecks.length; x++) {
        allChecks[x].checked = false;
      }
      if (profile.permissions) {
        newState.checkedPermissions = [];

        for (let i = 0; i < state.permissionsList.length; i++) {
          const group = state.permissionsList[i];
          for (let x = 0; x < group.itens.length; x++) {
            newState.checkedPermissions.push({
              slug: group.itens[x].slug,
              active: profile.permissions.some(
                (perm) => perm.slug === group.itens[x].slug && perm.active,
              ),
            });
          }
        }
      }
      setState(newState);
    } catch (e) {
      setValues((previous) => ({ ...previous, selectedProfile: '' }));
      setState((previous) => ({ ...previous, checkedPermissions: [], profileSelected: null }));
      setSnackbar({
        display: true,
        type: 'error',
        message: 'Não foi possível carregar as permissões do perfil. Selecione o perfil novamente.',
      });
    } finally {
      setProfileLoading(false);
    }
  }

  const saveProfile = async () => {
    await profilePermissionSave();

    const { data } = await cooperplaceApi.get('users/permissions');
    localStorage.setItem(
      'permissions',
      JSON.stringify(data)
    );

    if (data) {
      localStorage.setItem('permissions', JSON.stringify(data));
      navigate('/permissoes-usuario');
    }
  };

  const profilePermissionSave = async () => {
    const { checkedPermissions } = state;
    const { selectedProfile } = values;
    if (!profileLoading && checkedPermissions !== null && selectedProfile) {
      try {
        setLoading(true);
        const response = await axios.post(
          `${config.server}users-profiles/${selectedProfile}/permissions`,
          { permissions: checkedPermissions },
        );
        if (response.status === 200) {
          setValues({ selectedProfile: null });
          setSnackbar({
            type: 'success',
            message: 'Perfil salvo com sucesso!',
            display: true,
          });
        }
      } catch (e) {
        setSnackbar({
          type: 'danger',
          message: e.response.data[0].message,
          display: true,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangeCheckBoxPermission = (slug) => {
    const newPermissions = state.checkedPermissions.map((item) =>
      item.slug === slug ? { ...item, active: !item.active } : item,
    );

    setState({ ...state, checkedPermissions: newPermissions });
  };

  function handleChangeSelect(event) {
    const profileId = event.target.value;

    setValues((oldValues) => ({
      ...oldValues,
      [event.target.name]: event.target.value,
    }));

    changeProfile(profileId);
  }
  const openDrawer = useCallback(() => {
    setFormOpened(true);
  }, []);

  const closeDrawer = useCallback(async (refresh = false) => {
    setFormOpened(false);
    if (refresh) {
      getProfileList();
      setValues({ selectedProfile: null });
    }
  }, []);

  const { leftColumn, rightColumn } = useMemo(() => {
    const left = [];
    const right = [];
    state.permissionsList.forEach((group, index) => {
      const groupIndex = Number.isInteger(group.originalIndex) ? group.originalIndex : index;
      if (groupIndex % 2 === 0) {
        left.push(group);
      } else {
        right.push(group);
      }
    });
    return { leftColumn: left, rightColumn: right };
  }, [state.permissionsList]);

  const permissionGroupTitleStyle = {
    color: '#6C7A89',
    fontWeight: 600,
    letterSpacing: '0.03em',
  };

  const renderPermissionCard = (group, index) => (
    <Card key={`${group.title}-${index}`} form={1} className={classes.permissionCard} style={{ minHeight: 220 }}>
      <div className={classes.permissionCardHeader}>
        <Label uppercase={1} nomargin={1} style={permissionGroupTitleStyle}>
          {group.title}
        </Label>
        <Tooltip content={<Text>{`Permissões do grupo ${group.title}`}</Text>} placement="left">
          <span>
            <FaRegLightbulb className={classes.permissionCardInfoIcon} />
          </span>
        </Tooltip>
      </div>
      <div className={classes.permissionCardDivider} />

      {group.itens.map((item) => (
        <div key={item.id} className={classes.permissionItemRow}>
          <Checkbox
            disabled={profileLoading || loading}
            checked={state.checkedPermissions.some(
              (perm) => perm.slug && perm.slug === item.slug && perm.active,
            )}
            onChange={() => handleChangeCheckBoxPermission(item.slug)}
            slug={item.slug}
            id={item.slug}
            label={item.name}
            labelStyle={{ width: '100%', marginRight: 8 }}
          />
          <Tooltip content={<Text>{item.description}</Text>}>
            <FaExclamationCircle className={classes.permissionItemInfo} />
          </Tooltip>
        </div>
      ))}
    </Card>
  );

  return (
    <ApplicationLayout
      isOld
      title="Permissões de Usuário"
      RightComponent={
        <Button className={classes.auditButton} onClick={() => setAuditOpen(true)}>
          <Text weight={600} color="#2F3B52" type="regular">
            Auditoria
          </Text>
        </Button>
      }
    >
      <ProfilesAuditModal isOpen={auditOpen} onClose={() => setAuditOpen(false)} />
      <Fab
        variant="extended"
        aria-label="Remover selecionados"
        outline
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '120px',
          zIndex: '1',
          border: '2px solid rgb(33, 150, 243)',
          color: '#FFF',
          paddingLeft: '20px',
          paddingRight: '20px',
          height: '36px',
          borderRadius: '18px',
          backgroundColor: 'rgb(33, 150, 243)',
        }}
        onClick={openDrawer}
      >
        Adicionar perfil de usuário
      </Fab>
      <Snackbar
        open={snackbar.display}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar(false)}
      />
      <form autoComplete="off">
        <Grid xs={12} md={6} lg={4}>
          <Select
            disabled={profileLoading || loading}
            name="selectedProfile"
            label={values.selectedProfile ? 'Perfil selecionado' : 'Selecione um perfil'}
            placeholder="Selecione"
            labelColor="#464E5F"
            textColor="#464E5F"
            value={values.selectedProfile}
            defaultValue={values.selectedProfile}
            onChange={handleChangeSelect}
            options={profileList}
            itemValue="id"
            itemTitle="name"
            noUnderline
          />
        </Grid>
      </form>

      {values.selectedProfile && (
        <>
          <div className={classes.permissionsSectionHeader}>
            <div className={classes.permissionsSectionHeaderRow}>
              <span className={classes.permissionsSectionTitle}>Permissões do perfil</span>
              <Tooltip
                placement="left"
                content={<Text>Marque ou desmarque as permissões e clique em salvar no final.</Text>}
              >
                <span>
                  <FaRegLightbulb className={classes.permissionsSectionIcon} />
                </span>
              </Tooltip>
            </div>
            <div className={classes.permissionsSectionDivider} />
          </div>

          <div className={classes.permissionsColumns}>
            <div className={classes.permissionsColumn}>
              {leftColumn.map((group, index) => renderPermissionCard(group, index))}
            </div>

            <div className={classes.permissionsColumn}>
              {rightColumn.map((group, index) => renderPermissionCard(group, index))}
            </div>
          </div>

          <Container
            style={{
              padding: '15px 0',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
            container="true"
          >
            <Button color="success" loading={loading} disabled={profileLoading} onClick={() => saveProfile()}>
              Salvar
            </Button>
          </Container>
        </>
      )}

      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={formOpened}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <FormCadastroPerfil
          open={formOpened}
          setSnackbar={setSnackbar}
          onClose={closeDrawer}
        />
      </Drawer>
    </ApplicationLayout>
  );
}
